import { faCircleNodes } from "@fortawesome/pro-regular-svg-icons";
import { Icon } from "@intility/bifrost-react";
import type { CSSProperties } from "react";
import type { PlatformLogoResponse } from "../../api/types";
import { useLogoRegistry } from "./useLogoRegistry";

// A platform's logo: a builtin logo is rendered from the @intility/logos
// registry by key, a custom one from its stored data URI. No logo falls back
// to the circle-nodes icon, the same as on a standalone client's Connect to
// platform button.
// Logos always render through <img src>; never inline.
export function PlatformLogo({
  logo,
  size = 32,
  style,
}: {
  logo: PlatformLogoResponse | null;
  size?: number;
  style?: CSSProperties;
}) {
  if (!logo) {
    return (
      <Icon
        icon={faCircleNodes}
        style={{ fontSize: size, color: "var(--bfc-base-c-theme)", ...style }}
        aria-label="Platform"
      />
    );
  }

  if (logo.type === "custom") {
    return (
      <img
        src={logo.data ?? undefined}
        alt=""
        style={{
          width: size,
          height: size,
          objectFit: "contain",
          ...style,
        }}
      />
    );
  }

  return <BuiltinPlatformLogo logo={logo} size={size} style={style} />;
}

// A builtin logo needs the registry, so only this subtree loads it: pages
// without a builtin logo never fetch the registry chunk.
function BuiltinPlatformLogo({
  logo,
  size,
  style,
}: {
  logo: PlatformLogoResponse;
  size: number;
  style?: CSSProperties;
}) {
  const registry = useLogoRegistry();

  // The registry chunk may still be loading. The placeholder keeps the
  // layout stable while it arrives.
  const dataUri = registry?.[logo.key ?? ""];
  if (!dataUri) {
    return (
      <Icon
        icon={faCircleNodes}
        style={{ fontSize: size, color: "var(--bfc-base-c-theme)", ...style }}
        aria-label="Platform"
      />
    );
  }

  return (
    <img
      src={dataUri}
      alt=""
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        ...style,
      }}
    />
  );
}
